import { IResponseProcessor } from "..";
import { ICategory } from "../category/CategoriesApi";
import { IOrderDetailResult, IPurchaseOrderPartNumberResult, IRequisitionPartNumberResult } from "../document/DocumentsApi";
import { IOpportunity } from "../opportunity/OpportunitiesApi";
import { ISalesStatistics } from "../reseller/ResellersApi";

/**
 * @deprecated Use RTK Query hooks from `randmar-api-client` instead. This class is part of the legacy MobX-based data fetching layer and will be removed in the future.
 */
export default class ProductsApi {
  constructor(
    private _baseUrl: string,
    private _getToken: (applicationId: string) => string,
    private _processor: IResponseProcessor
  ) { }

  /**
   * @deprecated Use `useGetV4ResellerByRouteResellerIdProductAndRandmarSkuQuery` or `useGetV4PartnerByRouteApplicationIdProductAndRandmarSkuQuery` from `randmar-api-client` instead.
   */
  async retrieveProduct(applicationId: string, randmarSKU: string, withSpecification: boolean): Promise<IProduct> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/Product/${randmarSKU}?withSpecification=${withSpecification}`, {
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    this._processor.process(res);

    const info: IProduct = await res.json();

    return info;
  }

  /**
   * @deprecated Use `usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberFieldNameMutation` from `randmar-api-client` instead.
   */
  async setProductProperty(applicationId: string, randmarSKU: string, propName: string, newValue: string): Promise<boolean> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/Product/${randmarSKU}/${propName}?newValue=${encodeURIComponent(newValue)}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    this._processor.process(res);

    return await res.json();
  }

  /**
   * @deprecated Use `usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberBodyHtmlMutation` from `randmar-api-client` instead.
   */
  async setProductBodyProperty(applicationId: string, randmarSKU: string, propName: string, newValue: string): Promise<boolean> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/Product/${randmarSKU}/${propName}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this._getToken(applicationId)}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newValue),
    });

    this._processor.process(res);

    return await res.json();
  }

  /**
   * @deprecated Use `usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberDiscontinueMutation` from `randmar-api-client` instead.
   */
  async discontinueProduct(applicationId: string, randmarSKU: string): Promise<boolean> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/Product/${randmarSKU}/Discontinue`, {
      method: "POST",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    this._processor.process(res);

    return await res.json();
  }

  /**
   * @deprecated Use `usePutV4ManufacturerByRouteManufacturerIdProductMutation` from `randmar-api-client` instead.
   */
  async addNewProduct(applicationId: string, _upc: string, product: IAddProduct): Promise<IProduct> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/Product`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(product)
    });

    this._processor.process(res);

    const info: IProduct = await res.json();

    return info;
  }

  /**
   * @deprecated Use `usePostV4ManufacturerByRouteManufacturerIdProductAndRandmarSku3DModelMutation` from `randmar-api-client` instead.
   */
  async addProduct3DModel(applicationId: string, randmarSKU: string, modelFile: File): Promise<boolean> {
    const dataFile = new FormData();
    dataFile.append('file', modelFile);

    const res = await fetch(
      `${this._baseUrl}/${applicationId}/Product/${randmarSKU}/3DModel`, {
      method: "POST",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` },
      body: dataFile
    });

    this._processor.process(res);

    return await res.json();
  }

  /**
   * @deprecated Use `usePostV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuImageMutation` from `randmar-api-client` instead.
   */
  async uploadProductPicture(applicationId: string, randmarSKU: string, selectedFile: File): Promise<boolean> {
    const dataFile = new FormData();
    dataFile.append('file', selectedFile);

    const res = await fetch(
      `${this._baseUrl}/${applicationId}/Product/${randmarSKU}/Image`, {
      method: "POST",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` },
      body: dataFile
    });

    this._processor.process(res);

    return await res.json();
  }

  /**
   * @deprecated Use `usePutV4ResellerByRouteResellerIdProductAndRandmarSkuRequisitionMutation` or `usePutV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuRequisitionMutation` from `randmar-api-client` instead.
   */
  async addToRequisition(applicationId: string, randmarSKU: string, location: string, quantity: string): Promise<boolean> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/Product/${randmarSKU}/Requisition?location=${location}&quantity=${quantity}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    return await res.json();
  }

  /**
   * @deprecated Use `usePutV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuInstantRebateMutation` from `randmar-api-client` instead.
   */
  async addInstantRebate(applicationId: string, randmarSKU: string, rebate: number, startDate: string, endDate: string, promotionId: string): Promise<boolean> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/Product/${randmarSKU}/InstantRebate`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${this._getToken(applicationId)}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ Rebate: rebate, StartDate: startDate, EndDate: endDate, PromotionId: promotionId })
    });

    this._processor.process(res);

    return await res.json();
  }
}

interface IWarehouseInventory {
  RandmarSKU: string;
  WarehouseId: string;
  WarehouseType: string;
  Name: string;
  City: string;
  Province: string;
  Country: string;
  AvailableQuantity: number;
  PurchaseOrderQuantity: number;
  BinLocation: string;
}

export interface IProduct {
  AutoUpdate: number;
  Category: string;
  CreatedBy: string;
  MPN: string;
  ManufacturerName: string;
  MasterCarton: number;
  ProductType: string;
  RandmarSKU: string;
  SerialNumber: boolean;
  TransparencyCode: boolean;
  UPC: string;
  UnitWeight: number;
  UnitHeight?: number | null;
  UnitLength?: number | null;
  UnitWidth?: number | null;
  SkidQuantity: number;
  IsManufacturer: boolean;
  BodyHTML: string;
  Title: string;
  MAP: number;
  MSRP: number;
  State: string;
  OpportunityOnly: boolean;
  VoiceoverCaption: string;
  SceneName: string;
  CountryCodeOfOrigin: string;
  Require3DScan: boolean;

  Distribution: {
    Price: number;
    RegularPrice: number;
    Cost: number;
    MAP: number;
    Currency: string;
    Inventory: IWarehouseInventory[];
  }
  InstantRebates?: IInstantRebate[];
  ActiveOrderDetails: IOrderDetailResult[];
  CompletedOrderDetails: IOrderDetailResult[];
  PurchaseOrders: IPurchaseOrderPartNumberResult[];
  Requisitions: IRequisitionPartNumberResult[];
  ManufacturerCategory: ICategory;
  Opportunities: IOpportunity[];
  SalesStatistics: ISalesStatistics[];
}

export interface IInstantRebate {
  RandmarSKU: string;
  RebateAmount: number;
  StartDate: string;
  EndDate: string;
  PromotionId: string;
}

export interface IAddProduct {
  UPC: string;
  MAP?: number;
  MSRP?: number;
  Title: string;
  MPN: string;
  Category: string;
  Description?: string;
  StandardCost: number;
  MasterCarton: number;
  SkidQuantity?: number;
  UnitWeight: number;
  BodyHTML?: string;
}

export interface ITransactionHistory {
  DocumentNumber?: string;
  Category?: string;
  Location?: string;
  TransactionDate?: number;
  TransactionType?: string;
  Quantity?: number;
  LinkedDocument: string;
}
